// Screen 2 — the flagship "Today's standup" report (AI-written, mirrored here).
import React from 'react';
import { Skeleton, Spin } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  RedoOutlined,
  ExportOutlined,
  EditOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Avatar, AreaTag, StatusPill, SourceChip, entryChips } from './parts';

function StatCard({ num, label, red }) {
  return (
    <div className={'ck21__stat' + (red ? ' ck21__stat--red' : '')}>
      <div className="num">{num}</div>
      <div className="lbl">{label}</div>
    </div>
  );
}

function Entry({ e }) {
  const d = e.draft || {};
  const lowConfidence = d.confident === false || d.source === 'no_signal';
  const chips = entryChips(e);
  return (
    <div className="ck21__entry">
      <Avatar name={e.name} seed={e.user_id || e.slack_user_id || e.name} />
      <div style={{ flex: 1 }}>
        <div className="ck21__ehead">
          <span className="ck21__ename">{e.name}</span>
          <AreaTag area={e.area} />
          {!lowConfidence && <StatusPill status={d.status} />}
        </div>

        {lowConfidence ? (
          <div className="ck21__amber">
            <InfoCircleOutlined style={{ color: '#B07D10' }} />
            <span>
              {d.source === 'no_signal'
                ? `No activity across Jira, GitHub or Slack yesterday. I'll ask ${
                    (e.name || '').split(' ')[0]
                  } directly.`
                : d.line ||
                  `Couldn't tell what ${
                    (e.name || '').split(' ')[0]
                  } worked on — I'll ask directly.`}
            </span>
          </div>
        ) : (
          <>
            <div className="ck21__line">{d.line}</div>
            {d.today && (
              <div className="ck21__next">
                <b>Next:</b> {d.today}
              </div>
            )}
            {d.status === 'blocked' && d.blockers && (
              <div className="ck21__blocker">
                <b>Blocker:</b> {d.blockers}
              </div>
            )}
          </>
        )}

        {chips.length > 0 && (
          <div className="ck21__chiprow">
            {chips.map((c, i) => (
              <SourceChip key={i} kind={c.kind} label={c.label} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function StandupReport({
  report,
  loading,
  dateLabel,
  onPrev,
  onNext,
  onRerun,
  onViewSlack,
  onEdit,
  onAllCheckins,
}) {
  const r = report || {};
  const flow = r.flow || {};
  const sh = r.signal_health || {};
  const reportedMeta = r.meta || {};
  const sources = ['Jira', 'GitHub', 'Slack'];
  const connected = sources.filter((s) => sh[s.toLowerCase()] === true);
  const noSources = connected.length === 0;

  return (
    <div className="ck21__inner">
      <div className="ck21__subhead">
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ck21__navbtn" onClick={onPrev}>
            <LeftOutlined />
          </button>
          <button className="ck21__navbtn" onClick={onNext}>
            <RightOutlined />
          </button>
        </div>
        <div className="ck21__rtitle">Today's standup · {dateLabel}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          <button className="ck21__navbtn" onClick={onRerun} title="Re-run">
            <RedoOutlined />
          </button>
          <button className="ck21__navbtn" onClick={onViewSlack} title="View in Slack">
            <ExportOutlined />
          </button>
          <button className="ck21__navbtn" onClick={onEdit} title="Edit configuration">
            <EditOutlined />
          </button>
        </div>
      </div>

      <div className="ck21__meta">
        <span>
          Posted to <b>#{(r.team && r.team.channel && r.team.channel.name) || '—'}</b>
        </span>
        {reportedMeta.reported != null && (
          <span>
            · <b>{reportedMeta.reported}</b> reported
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: connected.length ? '#1B9E57' : '#A6AEB8' }}>
          ● Reading {connected.join(' · ') || 'no sources connected'}
        </span>
      </div>

      {loading ? (
        <>
          <div className="ck21__stats">
            {[0, 1, 2, 3, 4].map((i) => (
              <div className="ck21__stat" key={i}>
                <Skeleton active paragraph={false} title={{ width: '60%' }} />
              </div>
            ))}
          </div>
          <div className="ck21__center">
            <Spin />
            <div style={{ marginTop: 14 }}>
              Troopr is reading everyone's Jira, GitHub and Slack activity…
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="ck21__stats">
            <StatCard num={flow.in_progress || 0} label="In progress" />
            <StatCard num={flow.in_review || 0} label="In review" />
            <StatCard num={flow.ready_to_deploy || 0} label="Ready to deploy" />
            <StatCard num={flow.shipped || 0} label="Shipped since yesterday" />
            <StatCard num={flow.blocked || 0} label="Blockers" red />
          </div>

          {noSources ? (
            <div className="ck21__infobanner" style={{ marginBottom: 18 }}>
              <InfoCircleOutlined style={{ color: '#97A0AC', fontSize: 17 }} />
              Troopr isn't reading any tools for this team yet. Connect Jira,
              GitHub or Slack in <b>&nbsp;Connections</b>, then re-run — it'll
              write each person's update from their activity automatically.
            </div>
          ) : (
            r.needs_attention &&
            r.needs_attention.length > 0 && (
              <div className="ck21__callout ck21__callout--red">
                <div className="hd">
                  <WarningOutlined /> NEEDS ATTENTION
                </div>
                {r.needs_attention.map((n, i) => (
                  <div className="ln" key={i}>
                    <b>{n.name}</b> {n.why}
                  </div>
                ))}
              </div>
            )
          )}

          <div className="ck21__card ck21__people">
            <div className="ck21__seclabel">PER-PERSON UPDATES</div>
            {(r.entries || []).map((e, i) => (
              <Entry e={e} key={i} />
            ))}
            {(!r.entries || r.entries.length === 0) && (
              <div className="ck21__center">No updates yet for this run.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
